import { formatMoney } from '@expenditure/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Icon } from '../components/Icon';
import { CategoryIcon, ErrorState, HeroAmount, Money, PageSkeleton, SectionTitle, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { api } from '../lib/api';
import { errorMessage, formatDate, formatDayHeader } from '../lib/format';
import { confirmDialog, haptic } from '../lib/telegram';
import { useExpenses, useGroup, useInvalidateGroup, useRecurring } from '../lib/queries';
import type { Expense, Recurring } from '../lib/types';

export function GroupDetailPage() {
  const { groupId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const group = useGroup(groupId);
  const expenses = useExpenses(groupId);

  const days = useMemo(() => {
    const byDay = new Map<string, Expense[]>();
    for (const page of expenses.data?.pages ?? []) {
      for (const e of page.expenses) byDay.set(e.expenseDate, [...(byDay.get(e.expenseDate) ?? []), e]);
    }
    return [...byDay.entries()];
  }, [expenses.data]);

  if (group.isPending) return <PageSkeleton />;
  if (group.error) return <ErrorState message={errorMessage(group.error, t)} onRetry={() => void group.refetch()} />;

  const { group: g, summary, me, members, formerMembers } = group.data;
  const names = new Map([...formerMembers, ...members].map((m) => [m.id, m.displayName]));
  const net = summary.myNetMinor;

  return (
    <main className="page page--no-tabbar app">
      <section className="hero">
        <div className="hero-cards" aria-hidden>
          <i />
          <i />
          <i />
        </div>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div className="grow">
            <div className="hero-label">{t('common.total')}</div>
            <HeroAmount minor={summary.totalSpentMinor} currency={g.currency} />
          </div>
          <span className="hero-pill hero-pill--icon" aria-label={t('common.members', { count: summary.memberCount })}>
            <Icon name="members" size={14} />
            {summary.memberCount}
          </span>
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 700 }}>{g.name}</h1>
        {g.description && <div className="hero-label">{g.description}</div>}
        <div className="hero-sub">
          <div className="hero-pill">
            {t('group.average')}
            <strong className="num">{t('group.perPerson', { amount: formatMoney(summary.averagePerMemberMinor, g.currency) })}</strong>
          </div>
        </div>
      </section>

      <section className="stat-grid">
        <div className="stat">
          <div className="stat-label">{t('group.youPaid')}</div>
          <div className="stat-value num">{formatMoney(summary.myPaidMinor, g.currency)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">{t('group.yourShare')}</div>
          <div className="stat-value num">{formatMoney(summary.myShareMinor, g.currency)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">
            {net > 0 ? t('group.youShouldReceive') : net < 0 ? t('group.youShouldPay') : t('group.youAreSettled')}
          </div>
          <div className="stat-value">
            <Money minor={net} currency={g.currency} tone absolute />
          </div>
        </div>
      </section>

      <div className="quick-actions quick-actions--4">
        <Link className="quick-action" to={`/groups/${groupId}/expenses/new`}>
          <span className="qa-icon">
            <Icon name="plus" size={18} />
          </span>
          <span>{t('group.addExpense')}</span>
        </Link>
        <Link className="quick-action" to={`/groups/${groupId}/balances`}>
          <span className="qa-icon">
            <Icon name="chart" size={18} />
          </span>
          <span>{t('group.balances')}</span>
        </Link>
        <Link className="quick-action" to={`/groups/${groupId}/settle`}>
          <span className="qa-icon">
            <Icon name="swap" size={18} />
          </span>
          <span>{t('group.settleUp')}</span>
        </Link>
        <Link className="quick-action" to={`/groups/${groupId}/members`}>
          <span className="qa-icon">
            <Icon name="members" size={18} />
          </span>
          <span>{t('group.members')}</span>
        </Link>
      </div>

      {summary.expenseCount > 0 && (
        <Link className="card list-row" to={`/groups/${groupId}/insights`} style={{ minHeight: 0 }}>
          <span className="icon-tile icon-tile--ink" aria-hidden>
            <Icon name="trending" size={20} />
          </span>
          <span className="grow list-title">{t('insights.title')}</span>
          <span className="muted" aria-hidden>
            <Icon name="chevronRight" size={18} />
          </span>
        </Link>
      )}

      <RecurringSection groupId={groupId} />

      <SectionTitle>{t('group.history')}</SectionTitle>
      <div className="card card--flush">
        {expenses.isPending ? (
          <div className="empty">
            <Spinner />
          </div>
        ) : expenses.error ? (
          <ErrorState message={errorMessage(expenses.error, t)} onRetry={() => void expenses.refetch()} />
        ) : days.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden>
              <Icon name="receipt" size={28} />
            </div>
            <h3>{t('group.noExpenses')}</h3>
            <p>{t('group.noExpensesHint')}</p>
          </div>
        ) : (
          days.map(([day, items]) => (
            <div key={day}>
              <div className="day-header">{formatDayHeader(day, i18n.language, t)}</div>
              <div className="list">
                {items.map((e) => (
                  <ExpenseRow key={e.id} expense={e} myMemberId={me.id} payerName={names.get(e.paidByMemberId)} />
                ))}
              </div>
            </div>
          ))
        )}
        {expenses.hasNextPage && (
          <button
            className="btn btn--ghost btn--block"
            disabled={expenses.isFetchingNextPage}
            onClick={() => void expenses.fetchNextPage()}
          >
            {t('group.loadMore')}
          </button>
        )}
      </div>
    </main>
  );
}

function ExpenseRow({ expense: e, myMemberId, payerName }: { expense: Expense; myMemberId: string; payerName?: string }) {
  const { t } = useTranslation();
  const myShare = e.splits.find((s) => s.memberId === myMemberId)?.amountMinor ?? 0;
  const iPaid = e.paidByMemberId === myMemberId;
  return (
    <Link className="list-row" to={`/groups/${e.groupId}/expenses/${e.id}`}>
      <CategoryIcon category={e.category} />
      <div className="grow">
        <div className="list-title ellipsis">{e.description}</div>
        <div className="list-sub ellipsis">
          {t('group.paidBy', { name: iPaid ? t('common.you') : (payerName ?? '—') })}
        </div>
      </div>
      <div className="list-end">
        <Money minor={e.amountMinor} currency={e.currency} className="list-title" />
        <div className="small">
          {iPaid ? (
            <span className="pos">
              {t('group.youLent')} {formatMoney(e.amountMinor - myShare, e.currency)}
            </span>
          ) : myShare > 0 ? (
            <span className="neg">
              {t('group.youBorrowed')} {formatMoney(myShare, e.currency)}
            </span>
          ) : (
            <span className="muted">{t('group.notInvolved')}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

/** Expenses added again every week or month; shown only when there are some. */
function RecurringSection({ groupId }: { groupId: string }) {
  const { t, i18n } = useTranslation();
  const recurring = useRecurring(groupId);
  const invalidate = useInvalidateGroup();
  const toast = useToast();
  const [stopping, setStopping] = useState<string | null>(null);
  if (!recurring.data?.length) return null;

  const stop = async (item: Recurring) => {
    if (!(await confirmDialog(t('recurring.stopConfirm', { name: item.description })))) return;
    setStopping(item.id);
    try {
      await api.stopRecurring(groupId, item.id);
      haptic.success();
      await invalidate(groupId);
      toast(t('recurring.stopped'));
    } catch (error) {
      toast(errorMessage(error, t), 'error');
    } finally {
      setStopping(null);
    }
  };

  return (
    <>
      <SectionTitle>{t('recurring.title')}</SectionTitle>
      <div className="card card--flush list">
        {recurring.data.map((item) => (
          <div className="list-row" key={item.id} style={{ cursor: 'default' }}>
            <span className="icon-tile" aria-hidden>
              <Icon name="repeat" size={18} />
            </span>
            <div className="grow">
              <div className="list-title ellipsis">{item.description}</div>
              <div className="list-sub ellipsis">
                {t(`recurring.${item.frequency}`)} · {t('recurring.next', { date: formatDate(item.nextDate, i18n.language) })}
              </div>
            </div>
            <div className="list-end">
              <Money minor={item.amountMinor} currency={item.currency} className="list-title" />
              {item.canStop && (
                <button
                  type="button"
                  className="link-button small"
                  disabled={stopping === item.id}
                  onClick={() => void stop(item)}
                >
                  {t('recurring.stop')}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
